import { Controller } from "@hotwired/stimulus"
import { MELD_PROVIDER_ICONS, meldFormatProvider, MELD_PM_LABELS, meldQuoteValue } from "./meldProviderIcons.js"


export default class extends Controller {
    static targets = ["providerList", "continueBtnText", "selectedProviderInput", "selectedMethodInput"]

    // Translated copy written into the DOM by this controller. {provider} is
    // substituted with the chosen provider's display name.
    static values = { continueWith: String }

    connect() {
        const raw = sessionStorage.getItem("meld_quotes")
        if (!raw) {
            window.location.href = "/meld"
            return
        }

        this._quotes = this._sortQuotes(JSON.parse(raw))
        this._selectedIdx = 0

        const savedProvider = sessionStorage.getItem("meld_selected_provider")
        const savedMethod   = sessionStorage.getItem("meld_selected_method")
        if (savedProvider) {
            const idx = this._quotes.findIndex(q =>
                q.serviceProvider === savedProvider &&
                (!savedMethod || q.paymentMethodType === savedMethod)
            )
            if (idx >= 0) this._selectedIdx = idx
        }

        this._render()
    }

    select(event) {
        const idx = parseInt(event.currentTarget.dataset.idx, 10)
        if (isNaN(idx)) return
        this._selectedIdx = idx
        const q = this._quotes[idx]
        this.selectedProviderInputTarget.value = q.serviceProvider
        this.selectedMethodInputTarget.value   = q.paymentMethodType || ""
        this._highlightSelected()
        this._updateContinueBtn()
    }

    // ── Private ───────────────────────────────────────────────

    _sortQuotes(quotes) {
        return [...quotes].sort((a, b) => {
            // Same ranking the picker uses. Matters here because the list can come from the
            // express (reverse) quote, where each provider has its own sourceAmount.
            const vDiff = meldQuoteValue(b) - meldQuoteValue(a)
            if (vDiff !== 0) return vDiff

            const aLow = a.lowKyc ? 1 : 0
            const bLow = b.lowKyc ? 1 : 0
            if (bLow !== aLow) return bLow - aLow

            const rateDiff = (parseFloat(b.exchangeRate) || 0) - (parseFloat(a.exchangeRate) || 0)
            if (rateDiff !== 0) return rateDiff

            return (b.customerScore || 0) - (a.customerScore || 0)
        })
    }

    _render() {
        this.providerListTarget.innerHTML = this._quotes.map((q, i) => this._cardHTML(q, i)).join("")
        const selected = this._quotes[this._selectedIdx] || this._quotes[0]
        if (selected) {
            this.selectedProviderInputTarget.value = selected.serviceProvider
            this.selectedMethodInputTarget.value   = selected.paymentMethodType || ""
        }
        this._updateContinueBtn()
    }

    _cardHTML(q, i) {
        const selected  = i === this._selectedIdx
        const icon      = MELD_PROVIDER_ICONS[q.serviceProvider] || ""
        const name      = meldFormatProvider(q.serviceProvider)
        const method    = MELD_PM_LABELS[q.paymentMethodType] || q.paymentMethodType || ""
        const destAmt   = parseFloat(q.destinationAmount || 0)
        // Shown as dollars, matching the picker's "You receive": the destination is a dollar
        // stablecoin, and "$114.50" is read at a glance where "114.50 USDC" is decoded.
        const amtStr    = destAmt > 0
            ? "$" + new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(destAmt)
            : "—"

        const borderCls = selected
            ? "dark:border-dark-primary-color wdark:border-wdark-primary-color"
            : "dark:border-dark-input-border wdark:border-wdark-input-border"

        const badge = i === 0
            ? `<span class="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase
                 dark:bg-dark-input-background wdark:bg-wdark-input-background
                 dark:text-dark-primary-color wdark:text-wdark-primary-color">
                 Recommended
               </span>`
            : ""

        return `
        <div data-action="click->meld-compare#select"
             data-idx="${i}"
             class="flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors
               dark:bg-dark-secondary-color wdark:bg-wdark-secondary-color ${borderCls}">
          <div class="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded overflow-hidden">
            ${icon}
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="font-semibold text-sm dark:text-dark-text-and-title wdark:text-wdark-text-and-title">${name}</span>
              ${badge}
            </div>
            <p class="text-xs mt-0.5 dark:text-dark-passive-color-text-1 wdark:text-wdark-passive-color-text-1">${method}</p>
          </div>
          <div class="flex-shrink-0 text-right">
            <p class="font-semibold text-sm dark:text-dark-text-and-title wdark:text-wdark-text-and-title">${amtStr}</p>
            <p class="text-xs dark:text-dark-passive-color-text-1 wdark:text-wdark-passive-color-text-1">you receive</p>
          </div>
        </div>`
    }

    _highlightSelected() {
        this.providerListTarget.querySelectorAll("[data-idx]").forEach((el, i) => {
            el.classList.remove(
                "dark:border-dark-primary-color", "wdark:border-wdark-primary-color",
                "dark:border-dark-input-border",  "wdark:border-wdark-input-border"
            )
            if (i === this._selectedIdx) {
                el.classList.add("dark:border-dark-primary-color", "wdark:border-wdark-primary-color")
            } else {
                el.classList.add("dark:border-dark-input-border", "wdark:border-wdark-input-border")
            }
        })
    }

    _updateContinueBtn() {
        if (!this._quotes.length) return
        const q = this._quotes[this._selectedIdx] || this._quotes[0]
        const pattern = this.continueWithValue || "Continue With {provider}"
        this.continueBtnTextTarget.textContent = pattern.replace("{provider}", meldFormatProvider(q.serviceProvider))
    }
}
